import { ClientUserRole } from './client-user.model';

export type ClientUserRolePresetId = 'OWNER' | 'ADMIN' | 'DEVELOPER' | 'GUEST';

export interface ClientUserRolePreset {
  id: ClientUserRolePresetId;
  label: string;
  description: string;
  roleCode: ClientUserRole;
  canCreateProjects: boolean;
  canViewFinances: boolean;
  canEditFinances: boolean;
}

// Flags are optional because the API omits them when false — absent means false,
// never "unchanged".
export interface RoleFlagValues {
  roleCode: string;
  canCreateProjects?: boolean;
  canViewFinances?: boolean;
  canEditFinances?: boolean;
}

// Every other combination the API can hold is reachable only through the advanced editor.
export const CLIENT_USER_ROLE_PRESETS: ClientUserRolePreset[] = [
  {
    id: 'OWNER',
    label: 'Owner',
    description: 'Full control over company settings, billing, and team.',
    roleCode: ClientUserRole.Owner,
    canCreateProjects: true,
    canViewFinances: true,
    canEditFinances: true
  },
  {
    id: 'ADMIN',
    label: 'Admin',
    description: 'Full control over all projects. Can invite developers and guests.',
    roleCode: ClientUserRole.Admin,
    canCreateProjects: true,
    canViewFinances: false,
    canEditFinances: false
  },
  {
    id: 'DEVELOPER',
    label: 'Developer',
    description: 'Can create new projects and be assigned to projects.',
    roleCode: ClientUserRole.NoAccess,
    canCreateProjects: true,
    canViewFinances: false,
    canEditFinances: false
  },
  {
    id: 'GUEST',
    label: 'Guest',
    description: 'Can NOT create projects. Can only be assigned to projects.',
    roleCode: ClientUserRole.NoAccess,
    canCreateProjects: false,
    canViewFinances: false,
    canEditFinances: false
  }
];

// The entire "which preset is highlighted" rule — the grid derives from it, so there
// is no preset-to-advanced synchronisation to keep correct.
export function matchRolePreset(value: RoleFlagValues): ClientUserRolePreset | null {
  if (!value) {
    return null;
  }

  return CLIENT_USER_ROLE_PRESETS.find((preset) =>
    preset.roleCode === value.roleCode
      && preset.canCreateProjects === !!value.canCreateProjects
      && preset.canViewFinances === !!value.canViewFinances
      && preset.canEditFinances === !!value.canEditFinances
  ) ?? null;
}

// Must cover every role the API can return, including the API-only BASIC_USER and
// READ_ONLY — an unmapped role falls through and renders its raw enum value.
export const CLIENT_USER_ROLE_LABELS: Record<string, string> = {
  [ClientUserRole.Owner]: 'Owner',
  [ClientUserRole.Admin]: 'Admin',
  [ClientUserRole.BasicUser]: 'Basic User',
  [ClientUserRole.ReadOnly]: 'Read Only',
  [ClientUserRole.NoAccess]: 'Developer'
};

// Helper function to get role label from roleCode
export function getClientUserRoleLabel(roleCode: string, canCreateProjects?: boolean): string {
  // Handle NO_ACCESS role based on canCreateProjects flag
  if (roleCode === ClientUserRole.NoAccess) {
    return canCreateProjects ? 'Developer' : 'Guest';
  }
  return CLIENT_USER_ROLE_LABELS[roleCode] || roleCode;
}

/**
 * `BASIC_USER` -> `Basic user`. Deliberately not getClientUserRoleLabel, which renames
 * roles into the product's vocabulary ('Developer' for NO_ACCESS) — the advanced editor
 * needs the API's own identity, only made legible.
 */
export function formatRoleCode(roleCode: string): string {
  if (!roleCode) {
    return '';
  }

  const spaced = roleCode.replace(/_/g, ' ').toLowerCase();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// These describe the role ONLY. The three permission flags are set independently and
// are explained by their own controls, so mentioning them here would state something
// the role does not actually decide.
export const CLIENT_USER_ROLE_DESCRIPTIONS: Record<string, string> = {
  [ClientUserRole.Owner]: 'Full control over all projects, company settings, billing, and team.',
  [ClientUserRole.Admin]: 'Full control over all projects. Can manage users below Admin. No access to billing.',
  [ClientUserRole.BasicUser]: 'Can deploy and operate all projects, but cannot delete them or manage the team.',
  [ClientUserRole.ReadOnly]: 'Can view all projects, their logs and metrics, but cannot change anything.',
  [ClientUserRole.NoAccess]: 'No access by default. Can only reach projects they are individually assigned to.'
};

// Unknown codes yield '' so the caller can suppress the tooltip rather than show an
// empty bubble.
export function getRoleDescription(roleCode: string): string {
  if (!roleCode) {
    return '';
  }
  return CLIENT_USER_ROLE_DESCRIPTIONS[roleCode] || '';
}


